"use client";

import { MetronomePulseDots } from "@/components/ui/atoms/metronome-pulse-dots";
import { MetronomeSignaturePicker } from "@/components/ui/molecules/metronome-signature-picker";
import { MetronomeDivisionPicker } from "@/components/ui/molecules/metronome-division-picker";
import { VolumeSlider } from "@/components/ui/molecules/volume-slider";
import type { DivisionId, Signature } from "@/lib/studio/studio";

interface MetronomeSectionProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;

  signature: Signature;
  onSignatureChange: (s: Signature) => void;
  /** When true, the signature is dictated by the loaded piece. */
  signatureLocked: boolean;

  division: DivisionId;
  onDivisionChange: (id: DivisionId) => void;

  accent: boolean;
  onAccentChange: (accent: boolean) => void;

  volume: number;
  onVolumeChange: (v: number) => void;
}

const SECTION_LABEL_CLASS =
  "text-[11px] uppercase tracking-wider text-text-muted font-medium";

const SUBSECTION_LABEL_CLASS =
  "text-[10px] uppercase tracking-wider text-text-muted font-medium";

const TOGGLE_PILL_BASE =
  "px-2.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider transition-colors duration-150 cursor-pointer select-none";

export function MetronomeSection({
  enabled,
  onToggle,
  signature,
  onSignatureChange,
  signatureLocked,
  division,
  onDivisionChange,
  accent,
  onAccentChange,
  volume,
  onVolumeChange,
}: MetronomeSectionProps) {
  return (
    <section className="flex flex-col gap-3">
      {/* Header, title · pulse dots · Off/On toggle */}
      <div className="flex items-center justify-between">
        <span className={SECTION_LABEL_CLASS}>Métronome</span>

        <div className="flex items-center gap-2">
          <MetronomePulseDots
            signature={signature}
            running={enabled}
            accent={accent}
            size={7}
            gap={4}
          />

          <div
            role="group"
            aria-label="Activer le métronome"
            className="flex items-center gap-0.5 p-0.5 rounded-md border border-border bg-bg-deep"
          >
            <button
              type="button"
              aria-pressed={!enabled}
              onClick={() => onToggle(false)}
              className={`${TOGGLE_PILL_BASE} ${
                enabled
                  ? "bg-transparent text-text-muted"
                  : "bg-text-primary text-bg-deep"
              }`}
            >
              Off
            </button>
            <button
              type="button"
              aria-pressed={enabled}
              onClick={() => onToggle(true)}
              className={`${TOGGLE_PILL_BASE} ${
                enabled
                  ? "bg-[var(--color-accent)] text-white"
                  : "bg-transparent text-text-muted"
              }`}
            >
              On
            </button>
          </div>
        </div>
      </div>

      {/* Signature */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className={SUBSECTION_LABEL_CLASS}>Signature</span>
          {signatureLocked ? (
            <span className="text-[9px] font-mono italic text-text-secondary">
              ♪ depuis le morceau
            </span>
          ) : null}
        </div>

        <MetronomeSignaturePicker
          value={signature}
          onChange={onSignatureChange}
          locked={signatureLocked}
        />
      </div>

      {/* Division */}
      <div className="flex flex-col gap-1.5">
        <span className={SUBSECTION_LABEL_CLASS}>Division</span>
        <MetronomeDivisionPicker value={division} onChange={onDivisionChange} />
      </div>

      {/* Accent toggle + Volume */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          role="switch"
          aria-checked={accent}
          onClick={() => onAccentChange(!accent)}
          className={[
            "flex items-center gap-2 px-2.5 py-1.5 rounded-md border transition-colors duration-150 cursor-pointer text-left",
            accent
              ? "border-[rgba(251,191,36,0.30)] bg-[var(--color-accent-warm-subtle)]"
              : "border-border bg-bg-deep hover:border-border-subtle",
          ].join(" ")}
        >
          <span
            aria-hidden
            className="inline-block w-2 h-2 rounded-full"
            style={{
              background: accent
                ? "var(--color-accent-warm)"
                : "var(--color-text-muted)",
              boxShadow: accent
                ? "0 0 6px var(--color-accent-warm-glow)"
                : "none",
              transition: "background 150ms, box-shadow 150ms",
            }}
          />
          <span className="flex flex-col leading-tight">
            <span className="text-[11px] text-text-primary">
              Accent sur le 1<sup className="text-[7px]">er</sup>
            </span>
            <span className="text-[9px] italic text-text-muted">
              son aigu
            </span>
          </span>
        </button>

        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border bg-bg-deep">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">
            Vol
          </span>
          <div className="flex-1 min-w-0 flex items-center">
            <VolumeSlider value={volume} onChange={onVolumeChange} />
          </div>
          <span className="text-[10px] tabular-nums text-text-secondary w-6 text-right">
            {Math.round(volume)}
          </span>
        </div>
      </div>
    </section>
  );
}
